'use client';

import { useEffect, useRef } from 'react';

import { CONSENT_WITHDRAWN_EVENT_TYPE, PULSE_CONSENT_CHANGED_EVENT, PULSE_CONSENT_COOKIE_NAME } from '../consent.js';
import type { PulseTracker } from '../tracker.js';

import { watchConsentWithdrawal } from './consent-reader.js';
import { readCookieConsent } from './read-cookie-consent.js';

/**
 * Sends the one explicit withdrawal when a visitor turns analysis cookies off (CIN-14858).
 *
 * **Why an event at all, when the tracker simply stops.** ADR 0018: silence is indistinguishable
 * from inactivity. A visitor who withdraws and one who closes the tab look identical from the
 * backend, so a withdrawal has to be said out loud — it is what flips the identity's status and
 * writes the `interest_consent_log` row that records `granted → withdrawn`. Forward-only, per GDPR
 * Art 7(3): nothing collected before the withdrawal is erased by it.
 *
 * **Why not the Identity endpoint**, which has a `consentStatus` field: a partner surface holds an
 * ingest-only token and no Identity id, so it could never call it, and two mechanisms for one state
 * would give `interest_consent_log` two entry points. That field stays for admin correction.
 *
 * **What it watches.** `readConsent` — the same reader the tracker gates on, so a partner's CMP is
 * observed rather than a cookie it never writes. Without one it reads the consent cookie,
 * `cookieName`. The latest function passed is the one called, so a reader backed by render state is
 * never stale.
 *
 * Mounted inside {@link PulseProvider}, so both apps get it without touching their provider trees,
 * and a partner gets it by mounting the provider at all. A partner surface keeps it working by
 * writing its CMP's decision through {@link syncPulseConsent}, which dispatches on revoke as well
 * as on grant; a cookie written without the event is a withdrawal this hook never sees.
 */
export type PulseConsentWithdrawalOptions = {
    /** The `window` event the banner or {@link syncPulseConsent} dispatches. Defaults to Cineamo's. */
    eventName?: string;

    /** The consent cookie to watch when no `readConsent` is passed. Defaults to Cineamo's banner cookie. */
    cookieName?: string;

    /** Mounts nothing, for a surface whose consent cannot change without a reload. */
    disabled?: boolean;

    /** The reader the tracker gates on. Takes precedence over `cookieName`. */
    readConsent?: () => boolean;
};

export function usePulseConsentWithdrawal(
    tracker: PulseTracker,
    {
        eventName = PULSE_CONSENT_CHANGED_EVENT,
        cookieName = PULSE_CONSENT_COOKIE_NAME,
        disabled = false,
        readConsent
    }: PulseConsentWithdrawalOptions = {}
) {
    // A ref so a new reader identity each render does not tear down and re-subscribe the listener,
    // which would also reset what consent was last seen to be.
    const readerRef = useRef(readConsent);
    useEffect(() => {
        readerRef.current = readConsent;
    });

    useEffect(() => {
        if (disabled) return;

        return watchConsentWithdrawal({
            readConsent: () => (readerRef.current ? readerRef.current() : readCookieConsent(cookieName)),
            eventName,
            target: window,
            onWithdrawn: () => void tracker.track({ event_type: CONSENT_WITHDRAWN_EVENT_TYPE })
        });
    }, [tracker, eventName, cookieName, disabled]);
}
